import { ReactNode, useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { DetailDrawer } from '@rossum/ui';
import { Button, Stack } from '@rossum/ui/material';

type PreviewDrawerProps = {
  onClose: () => void;
  open: boolean;
  title: string;
  subtitle?: string;
  content: ReactNode;
  actions?: ReactNode;
};

export const PreviewDrawer = ({
  onClose,
  open,
  title,
  subtitle,
  content,
  actions,
}: PreviewDrawerProps) => {
  const intl = useIntl();

  const handleCloseEscPress = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleCloseEscPress);

    return () => {
      document.removeEventListener('keydown', handleCloseEscPress);
    };
  }, [handleCloseEscPress]);

  return (
    <DetailDrawer
      title={title}
      description={subtitle}
      open={open}
      onClose={() => onClose()}
    >
      <Stack
        padding={3}
        height="100%"
        spacing={2}
        justifyContent="space-between"
      >
        <Stack sx={{ flex: '1 1 100%', overflowY: 'scroll' }}>{content}</Stack>
        <Stack sx={{ flex: '0 0 auto' }}>
          <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
            {actions || (
              <Button
                variant="outlined"
                color="secondary"
                onClick={onClose}
                data-cy="enum-options-drawer-cancel-btn"
              >
                {intl.formatMessage({
                  id: 'features.fieldManager.overview.ui.previewDrawer.button.close',
                })}
              </Button>
            )}
          </Stack>
        </Stack>
      </Stack>
    </DetailDrawer>
  );
};
